import { useState } from 'preact/hooks'
import { useCrmSuppressionSources } from '@/hooks/useCrmSuppressionSources'
import { useToast } from '@/hooks/useToast'
import { ConfirmationModal } from '@/components'
import { AddCrmSuppressionSourceModal } from './AddCrmSuppressionSourceModal'
import type {
  CrmSuppressionProvider,
  CrmSuppressionSourceViewDto,
  CrmSuppressionSyncStatus,
} from '@/types/crmSuppressionSources'
import { isReversible } from '@/types/crmSuppressionSources'

interface Props {
  clientCompanyId: string
  /** Only this CRM's sources are shown — the panel sits under one connected card. */
  provider: CrmSuppressionProvider
}

/**
 * The "Suppression sources" section under a connected HubSpot or Salesforce card: what the client has
 * pointed us at, how the last run went, and the controls to add, run or remove one.
 */
export function CrmSuppressionSourcesPanel({ clientCompanyId, provider }: Props) {
  const { sources, loading, error, reload, add, remove, sync } = useCrmSuppressionSources(clientCompanyId)
  const { showSuccess, showError } = useToast()
  const [isAdding, setIsAdding] = useState(false)
  const [pendingRemoval, setPendingRemoval] = useState<CrmSuppressionSourceViewDto | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const forProvider = sources.filter(source => source.Provider === provider)

  const handleSync = async (source: CrmSuppressionSourceViewDto) => {
    setBusyId(source.Id)
    const result = await sync(source.Id)
    setBusyId(null)
    if (result.ok) {
      // Queued, not finished — saying "synced" here would be a lie the row itself then contradicts.
      showSuccess('Sync queued', 'The counts below update once it finishes.')
    } else if (!result.handled) {
      showError('Could not start the sync', result.error ?? 'Please try again.')
    }
  }

  const handleRemove = async () => {
    if (!pendingRemoval) return
    const source = pendingRemoval
    setPendingRemoval(null)
    setBusyId(source.Id)
    const result = await remove(source.Id)
    setBusyId(null)
    if (result.ok) showSuccess('Source removed', 'Addresses it suppressed remain on your suppression list.')
    else if (!result.handled) showError('Could not remove the source', result.error ?? 'Please try again.')
  }

  return (
    <section className="mt-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Suppression sources</h4>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Point us at where your do-not-email truth lives in {provider}. Matches land in your ordinary
            suppression list, checked before every marketing send.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => { void reload() }}
            disabled={loading}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="rounded-md bg-slate-900 dark:bg-white px-2.5 py-1.5 text-xs font-medium text-white dark:text-slate-900 hover:opacity-90"
          >
            Add source
          </button>
        </div>
      </header>

      {error && (
        <p className="mt-3 text-xs text-rose-600 dark:text-rose-400">{error}</p>
      )}

      {/* Suppressed while `error` is set: a failed load leaves the list empty, so without this the
          panel states as fact that the company has no sources when it simply could not find out. */}
      {!loading && !error && forProvider.length === 0 && (
        <p className="mt-4 rounded-md border border-dashed border-slate-300 dark:border-slate-700 px-4 py-6 text-center text-xs text-slate-500 dark:text-slate-400">
          No suppression sources yet. Add one to mirror a {provider} list into your suppression list.
        </p>
      )}

      <ul className="mt-3 space-y-2">
        {forProvider.map(source => (
          <li
            key={source.Id}
            className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2.5"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                  {source.DisplayName || source.ExternalId || source.Kind}
                </p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {source.Kind} · {source.Grain === 'Company' ? 'Companies' : 'People'} · {source.Reason}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {isReversible(source.Reason)
                    ? 'Removing someone from this source un-suppresses them on the next run.'
                    : 'Removing someone from this source leaves them suppressed — only an admin can clear these.'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <SyncStatusBadge status={source.LastStatus} message={source.LastStatusMessage} />
                <button
                  type="button"
                  onClick={() => { void handleSync(source) }}
                  disabled={busyId === source.Id}
                  className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                  Sync now
                </button>
                <button
                  type="button"
                  onClick={() => setPendingRemoval(source)}
                  disabled={busyId === source.Id}
                  className="rounded-md px-2 py-1 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            </div>

            <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex gap-1">
                <dt>Last synced</dt>
                <dd className="font-medium text-slate-700 dark:text-slate-300">
                  {source.LastSyncedUtc ? new Date(source.LastSyncedUtc).toLocaleString() : 'Never'}
                </dd>
              </div>
              <div className="flex gap-1">
                <dt>In source</dt>
                <dd className="font-medium text-slate-700 dark:text-slate-300">{source.LastMemberCount.toLocaleString()}</dd>
              </div>
              <div className="flex gap-1">
                <dt>Suppressed</dt>
                <dd className="font-medium text-slate-700 dark:text-slate-300">{source.LastSuppressedCount.toLocaleString()}</dd>
              </div>
              <div className="flex gap-1">
                <dt>Released</dt>
                <dd className="font-medium text-slate-700 dark:text-slate-300">{source.LastReleasedCount.toLocaleString()}</dd>
              </div>
            </dl>

            {source.LastStatusMessage && source.LastStatus !== 'Succeeded' && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{source.LastStatusMessage}</p>
            )}
          </li>
        ))}
      </ul>

      {isAdding && (
        <AddCrmSuppressionSourceModal
          clientCompanyId={clientCompanyId}
          provider={provider}
          onClose={() => setIsAdding(false)}
          onSave={add}
        />
      )}

      <ConfirmationModal
        isOpen={pendingRemoval !== null}
        title="Remove this suppression source?"
        // Said before confirming, because the opposite is the intuitive assumption: people expect
        // removing the source to undo what it did.
        message={
          'Addresses this source already suppressed stay suppressed — they remain on your suppression list ' +
          'and can be removed individually there. Only the link to your CRM is removed.'
        }
        type="warning"
        confirmText="Remove source"
        onConfirm={() => { void handleRemove() }}
        onCancel={() => setPendingRemoval(null)}
      />
    </section>
  )
}

function SyncStatusBadge({ status, message }: { status: CrmSuppressionSyncStatus; message?: string | null }) {
  const style =
    status === 'Succeeded' ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 ring-emerald-600/20'
      : status === 'Permission Denied' ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 ring-amber-600/20'
      : status === 'Failed' ? 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 ring-rose-600/20'
      : 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 ring-slate-500/20'

  // "Reconnect needed" rather than "Permission Denied": it is the one status the client can act on,
  // and naming the action is what stops it reading as an empty list.
  const label = status === 'Permission Denied' ? 'Reconnect needed' : status

  return (
    <span
      title={message ?? undefined}
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${style}`}
    >
      {label}
    </span>
  )
}
